import React, { useEffect, useState } from "react";
import { api, apiErr } from "../../lib/api";
import { formatNum } from "../../lib/utils";
import { Button } from "../ui/button";
import { Input, Label } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { toast } from "sonner";
import { Factory, CheckCircle2 } from "lucide-react";
import { TYPE_ICON, StatusChip, currentMonth } from "./shared";

export function CompositionTab({ comp, year }) {
  if (!comp) return <div className="card-flat p-6 text-sm text-muted-foreground" data-testid="ghg-comp-loading">Loading composition…</div>;
  const total = comp.total_scope1_tonnes || 0;
  return (
    <div className="space-y-4">
      <div className="card-flat p-4 flex items-center justify-between flex-wrap gap-3" data-testid="ghg-comp-total">
        <div>
          <div className="label-eyebrow text-[10px]">Total Scope 1 · FY{year}</div>
          <div className="font-mono text-3xl font-bold" data-testid="ghg-comp-total-value">{formatNum(total, { decimals: 2 })} <span className="text-sm text-muted-foreground">tCO₂e</span></div>
        </div>
        <div className="text-[11px] text-muted-foreground max-w-sm inline-flex items-center gap-1.5">
          <CheckCircle2 className="h-3.5 w-3.5 text-[#00E65B]" />
          {comp.reconciliation?.note}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {(comp.source_types || []).map((st) => {
          const Icon = TYPE_ICON[st.key] || Factory;
          return (
            <div key={st.key} className="card-flat p-4" data-testid={`ghg-comp-type-${st.key}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-accent" />
                  <span className="font-display font-semibold text-sm">{st.label}</span>
                </div>
                <StatusChip status={st.status} testid={`ghg-comp-status-${st.key}`} />
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">{st.desc}</p>
              <div className="flex items-end justify-between gap-2 mt-3">
                <div className="font-mono text-2xl font-bold" data-testid={`ghg-comp-co2e-${st.key}`}>
                  {formatNum(st.co2e_tonnes, { decimals: 2 })}<span className="text-xs text-muted-foreground font-sans ml-1">tCO₂e</span>
                </div>
                <span className="text-xs text-muted-foreground">{st.share_pct}% · {st.records} rec</span>
              </div>
              <div className="h-1.5 rounded-full bg-surface-hover overflow-hidden mt-2">
                <div className="h-full rounded-full bg-accent" style={{ width: `${Math.min(100, st.share_pct)}%` }} />
              </div>
              {st.catalog_entries?.length > 0 && (
                <div className="mt-3 space-y-1.5 border-t border-border pt-2">
                  {st.catalog_entries.map((e) => (
                    <div key={e.code} className="flex items-center justify-between gap-2 text-[11px]" data-testid={`ghg-comp-entry-${e.code}`}>
                      <span className="truncate">{e.name}</span>
                      <StatusChip status={e.status} testid={`ghg-comp-entry-status-${e.code}`} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function AddRecordDialog({ open, onOpenChange, catalog, onSaved }) {
  const scope1 = (catalog || []).filter((e) => e.scope === 1);
  const [form, setForm] = useState({ catalog_code: "", value: "", period: currentMonth(), activity_type: "", facility: "" });
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setForm({ catalog_code: "", value: "", period: currentMonth(), activity_type: "", facility: "" }); setPreview(null); } }, [open]);

  const entry = scope1.find((e) => e.code === form.catalog_code);

  const pickEntry = (code) => {
    const e = scope1.find((x) => x.code === code);
    setForm((p) => ({ ...p, catalog_code: code, activity_type: p.activity_type || (e?.name || "") }));
    setPreview(null);
  };

  const body = () => ({
    period: form.period, source_type: entry?.source_type, factor_code: entry?.factor_code,
    value: Number(form.value), unit: entry?.unit, activity_type: form.activity_type,
    catalog_code: form.catalog_code, facility: form.facility || null,
    source_doc: "GHG Inventory — manual entry",
  });

  const doPreview = async () => {
    if (!entry || !form.value) return;
    try { const r = await api.post("/ghg/scope1/calc-preview", body()); setPreview(r.data); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const save = async () => {
    if (!entry || !form.value || !form.period) { toast.error("Pick a source, value and period"); return; }
    setSaving(true);
    try {
      await api.post("/ghg/scope1/record", body());
      toast.success("Scope 1 record captured");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ghg-add-dialog">
        <DialogHeader><DialogTitle>Add a Scope 1 record</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Source (from the shared GHG catalog)</Label>
            <Select value={form.catalog_code} onValueChange={pickEntry}>
              <SelectTrigger data-testid="ghg-add-catalog"><SelectValue placeholder="Choose a Scope 1 source" /></SelectTrigger>
              <SelectContent>
                {scope1.map((e) => <SelectItem key={e.code} value={e.code}>{e.name} · {e.unit}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Value ({entry?.unit || "unit"})</Label>
              <Input type="number" value={form.value} onChange={(e) => { setForm((p) => ({ ...p, value: e.target.value })); setPreview(null); }} data-testid="ghg-add-value" /></div>
            <div><Label>Period (YYYY-MM)</Label>
              <Input value={form.period} onChange={(e) => setForm((p) => ({ ...p, period: e.target.value }))} data-testid="ghg-add-period" /></div>
          </div>
          <div><Label>Activity label</Label>
            <Input value={form.activity_type} onChange={(e) => setForm((p) => ({ ...p, activity_type: e.target.value }))} data-testid="ghg-add-activity" /></div>
          <div><Label>Facility (optional)</Label>
            <Input value={form.facility} onChange={(e) => setForm((p) => ({ ...p, facility: e.target.value }))} data-testid="ghg-add-facility" /></div>
          {preview && (
            <div className="card-flat p-3 text-xs" data-testid="ghg-add-preview">
              <span className="font-mono text-lg font-bold text-accent">{formatNum(preview.co2e_tonnes, { decimals: 4 })} tCO₂e</span>
              <span className="text-muted-foreground ml-2">= {preview.input_value} {preview.unit} × {preview.factor_kgco2e} kgCO₂e ÷ 1000</span>
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={doPreview} disabled={!entry || !form.value} data-testid="ghg-add-preview-btn">Preview tCO₂e</Button>
            <Button variant="primary" onClick={save} disabled={saving} data-testid="ghg-add-save">{saving ? "Saving…" : "Save record"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
